import type { Me } from "lib/hooks/useMe"
import type { WarehouseSelectOption } from "lib/hooks/useWarehouses"

import { UserProductOrderStatus } from "./graphql"

export const getInitialOrderQueryParams = (args?: {
  me: Me
  warehouses: WarehouseSelectOption[]
}) => {
  if (!args) return

  const { me, warehouses } = args

  const params = new URLSearchParams()

  params.set("status", UserProductOrderStatus.Accepted)
  params.set("charged", "true")
  params.set("onHold", "false")

  const warehouseId =
    !me.warehouseId &&
    warehouses.find((warehouse) => warehouse.codeName === "SUPLIFUL")?.id

  if (warehouseId) {
    params.set("warehouseId", warehouseId)
  }

  return params.toString()
}

export const routes = {
  home: `/`,
  admins: `/admins`,
  carriers: `/fulfillment-settings/carriers`,
  orders: (args?: { me: Me; warehouses: WarehouseSelectOption[] }) => {
    const params = getInitialOrderQueryParams(args)
    return params ? `/orders?${params}` : `/orders`
  },
  todaysItems: (args?: { me: Me; warehouses: WarehouseSelectOption[] }) => {
    const params = getInitialOrderQueryParams(args)
    return params ? `/todays-items?${params}` : `/todays-items`
  },
  partnerOrders: `/partner/orders`,
  returnedOrders: "/returned-orders",
  order: (id: string) => `/orders/${id}`,
  partnerOrder: (id: string) => `/partner/orders/${id}`,
  products: `/products`,
  product: (slug: string) => `/products/${slug}`,
  extendedProducts: `/extended-products`,
  extendedProduct: (slug: string) => `/extended-products/${slug}`,
  productsStock: `/catalog/products-stock`,
  purchaseOrder: (id: string) => `/suppliers/${id}/purchase-order`,
  shippingPackages: `/shipping-packages`,
  userProducts: `/user-products`,
  userProduct: (id: string) => `/user-products/${id}`,
  userProductLabel: (id: string, orderLineId?: string) =>
    `/user-products/${id}/label${
      orderLineId ? `?orderLineId=${orderLineId}` : ""
    }`,
  users: `/users`,
  user: (id: string) => `/users/${id}`,
  labelRolls: (args?: { me: Me; warehouses: WarehouseSelectOption[] }) => {
    const warehouseIdFilter = () => {
      if (!args) return
      const { me, warehouses } = args
      const adminWarehouseId = me.warehouseId
      if (adminWarehouseId) return
      const suplifulWarehouseId = warehouses.find(
        (warehouse) => warehouse.codeName === "SUPLIFUL",
      )?.id
      return suplifulWarehouseId || warehouses[0]?.id
    }

    const warehouseId = warehouseIdFilter()
    return warehouseId
      ? `/fulfillment/label-rolls?warehouseId=${warehouseId}`
      : `/fulfillment/label-rolls`
  },
  labeling: (batchId?: string) =>
    batchId
      ? `/fulfillment/labeling?batchId=${batchId}`
      : "/fulfillment/labeling",
  labelSizes: `/catalog/label-sizes`,
  labelVerification: `/user-management/label-verification`,
  labelTransitions: `/user-management/label-transitions`,
  postPayments: `/user-management/post-payments`,
  batchPrinting: `/fulfillment/batch-printing?isSingleItemOrder=true`,
  sorting: `/fulfillment/sorting`,
  bulkShipping: `/fulfillment/bulk-shipping`,
  bulkShippingDetails: (bulkShippingId?: string) =>
    bulkShippingId
      ? `/fulfillment/bulk-shipping-details?bulkShippingId=${bulkShippingId}`
      : "/fulfillment/bulk-shipping-details",
  shipmentBatches: `/fulfillment/shipment-batches`,
  completedShipments: `/fulfillment/completed-shipments`,
  packaging: `/fulfillment-settings/packaging`,
  warehouseShelves: `/fulfillment-settings/warehouse-shelves`,
  warehouses: `/fulfillment-settings/warehouses`,
  systemNotifications: `/site-settings/system-notifications`,
  amazonFba: `/fulfillment/amazon-fba`,
  amazonFbaInvoices: `/user-management/amazon-fba-invoices`,
  bulkInvoices: `/user-management/bulk-invoices`,
}

export const isActive = (asPath: string, href: string) => {
  if (href === "/") {
    return asPath === href
  }

  const asPathURL = new URL(asPath, window.location.origin)
  const hrefURL = new URL(href, window.location.origin)

  return (
    asPathURL.pathname === hrefURL.pathname ||
    asPathURL.pathname.startsWith(hrefURL.pathname + "/")
  )
}
